import React from "react";
import { useTheme } from "../context/ThemeContext";
import UserTopbar from "../components/UserTopbar";

export default function Manual() {
  const { colors, isDarkMode } = useTheme();

  const manualContent = [
    { 
      title: "1. MANIFEST ACQUISITION", 
      subtitle: "ASSET SELECTION & AGENT ASSIGNMENT",
      icon: "📦",
      meta: "INITIATION",
      text: `Self-Selection: Browse the 'Available Manifests' terminal and use the 'Add to Cart' feature to select your desired logistics products.\n\nAlternatively, you may contact a Senior Shipping Agent via Live Chat to have a specific high-priority manifest assigned directly to your Account ID.` 
    },
    { 
      title: "2. APPROVAL PROTOCOL", 
      subtitle: "SYSTEM SYNCHRONIZATION",
      icon: "⚙️",
      meta: "SYNCHRONIZATION",
      text: `Once a manifest appears in your queue, you must manually click 'Accept'.\n\nThis synchronization step confirms your participation in the logistics chain and locks in the current yield rates for that specific asset. Unapproved manifests will remain in a pending state and will not generate returns.` 
    },
    { 
      title: "3. SETTLEMENT & CLEARANCE", 
      subtitle: "SECURE UPLINK & VERIFICATION",
      icon: "💳",
      meta: "CAPITALIZATION",
      text: `After approval, you will be automatically redirected to the Orders Ledger section. Navigate to your active manifest and click the approved product to access the 'Make Payment' button.\n\nSystem Options:\n1•  Digital Settlement: Click 'Make Payment' to display secure deposit details. Submit your TXID (Transaction ID) within the portal for automated verification.\n2•  Bank Transfer: Select the 'Bank' option to view institutional routing details for wire or window transfers.\n3•  Manual Clearance: For high-priority handling, select 'Manual Clearance' to be connected directly with Customer Support for immediate verification.\n\nUpon successful verification, funds are added to your internal wallet and automatically deducted for the product.` 
    },
    { 
      title: "4. SHIPPING & YIELD TIMELINE", 
      subtitle: "TRANSIT & MATURITY WINDOWS",
      icon: "🚢",
      meta: "EFFICIENCY",
      text: `• Domestic (USA): Transit and logistics are expedited, typically reaching completion within 24 hours.\n• International (Outside USA): The standard transit window is 1–3 days.\n\nDuring this time, all logistics are automated; no action is required. On Maturity (Day 3/4), your internal wallet will be automatically credited with the Principal + Yield.` 
    },
    { 
      title: "5. PARTNER PROBATIONARY PERIOD", 
      subtitle: "VETTING PROCESS",
      icon: "🤝",
      meta: "MANDATORY PROTOCOL",
      isSpecial: true,
      text: `To maintain network integrity, all new accounts must complete exactly 1 order per day for 3 consecutive days. This established pattern serves as the mandatory vetting period for new logistics partners.\n\nUpon completion of this 72-hour window, all frequency restrictions are lifted. Partners may then scale their operations and order volume according to their own preference.` 
    },
    { 
      title: "6. WITHDRAWALS", 
      subtitle: "CAPITAL LIQUIDATION",
      icon: "🏦",
      meta: "LIQUIDATION",
      text: `Settled funds can be requested for withdrawal via the Wallet Page. Standard transfers are mapped to your registered bank account.\n\nFor Digital Wallet (Crypto) settlements or high-volume transfers, coordinate directly with a Treasury Agent via Live Chat to ensure secure routing.`
    },
    { 
      title: "7. LIVE SUPPORT", 
      subtitle: "HUMAN OPERATIVE OVERRIDE",
      icon: "🎧",
      meta: "SUPPORT",
      text: `Our system utilizes advanced AI dispatchers for rapid initial routing.\n\nIf you require specialized assistance or wish to speak to a human operative immediately, simply type the word 'Human' in the support terminal to bypass automated responses.`
    }
  ];

  return (
    <div style={{ 
      minHeight: "100vh", 
      backgroundColor: isDarkMode ? "#05080a" : colors.bg, 
      paddingBottom: "160px", 
      backgroundImage: isDarkMode 
        ? `linear-gradient(rgba(5, 8, 10, 0.85), rgba(5, 8, 10, 0.85)), url('/assets/logo.png')` 
        : `linear-gradient(rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)), url('/assets/logo.png')`,
      backgroundSize: "450px",
      backgroundPosition: "center 300px",
      backgroundRepeat: "no-repeat",
      backgroundAttachment: "fixed"
    }}>
      <UserTopbar title="" />
      
      <div style={{ width: "92%", maxWidth: "800px", margin: "140px auto 20px auto" }}>
        
        {/* HEADER WITH FLOATING SHIP */}
        <div style={{ textAlign: 'center', marginBottom: '50px' }}>
          <div className="floating-ship" style={{ 
            fontSize: "55px", 
            marginBottom: "15px", 
            filter: "drop-shadow(0 0 15px rgba(0,242,255,0.4))",
            display: 'inline-block' 
          }}>🚢</div>
          
          <div style={{ display: 'block' }}>
            <div style={{ 
                display: 'inline-block',
                backgroundColor: `${colors.brand}15`,
                border: `1px solid ${colors.brand}40`,
                padding: '4px 14px',
                borderRadius: '20px',
                color: colors.brand,
                fontSize: '10px',
                fontWeight: '900',
                letterSpacing: '2px',
                marginBottom: '15px'
            }}>
                SECURED V2.6 PROTOCOL
            </div>
          </div>

          <h1 style={{ 
            color: isDarkMode ? "#fff" : colors.text, 
            marginBottom: "10px", 
            fontSize: 'clamp(28px, 6vw, 42px)', 
            fontWeight: '900', 
            letterSpacing: '-1.5px' 
          }}>
            Manifesting Operational Manual
          </h1>
          <p style={{ color: isDarkMode ? "#94a3b8" : colors.text, opacity: 0.8, fontSize: "14px", maxWidth: "550px", margin: "0 auto", lineHeight: "1.6" }}>
            Official standard operating procedures for asset acquisition, synchronization, and yield liquidation within the global network.
          </p>

          {/* UPDATED: TRANSLATOR WIDGET WITH GLOBAL ICON */}
          <div style={{ 
            marginTop: "30px", 
            display: "flex", 
            flexDirection: "column",
            alignItems: "center",
            gap: "10px"
          }}>
            <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px', 
                color: colors.brand, 
                fontSize: '11px', 
                fontWeight: '700',
                letterSpacing: '1px'
            }}>
                <span style={{ fontSize: '16px' }}>🌐</span> SELECT PREFERRED LANGUAGE
            </div>
            <div id="google_translate_element" style={{
              padding: "4px",
              borderRadius: "12px",
              backgroundColor: isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)",
              border: `1px solid ${colors.border}`,
              minWidth: "160px"
            }}></div>
          </div>
        </div>

        {/* CONTENT STEPS */}
        <div style={{ display: 'grid', gap: '20px' }}>
          {manualContent.map((item, index) => (
            <div 
              className={`manual-card ${item.isSpecial ? 'special-glow' : ''}`}
              key={index} 
              style={{ 
                borderRadius: "24px", 
                backgroundColor: isDarkMode ? "rgba(15, 20, 25, 0.75)" : "#ffffff",
                backdropFilter: "blur(12px)",
                border: item.isSpecial ? `1.5px solid ${colors.brand}` : `1px solid ${colors.border}`,
                padding: "25px",
                transition: 'all 0.3s ease',
                position: 'relative',
                boxShadow: item.isSpecial ? `0 0 25px ${colors.brand}25` : '0 4px 15px rgba(0,0,0,0.1)'
              }}
            >
              <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
                <div style={{ 
                  minWidth: "50px", 
                  height: "50px", 
                  backgroundColor: isDarkMode ? "rgba(255,255,255,0.05)" : `${colors.brand}10`,
                  borderRadius: "15px",
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: "24px",
                  border: `1px solid ${colors.border}`
                }}>
                  {item.icon}
                </div>
                
                <div style={{ flex: 1 }}>
                  <div style={{ color: colors.brand, fontSize: "10px", fontWeight: "900", letterSpacing: '1.5px', marginBottom: '4px' }}>
                    {item.meta}
                  </div>
                  <h3 style={{ color: isDarkMode ? "#fff" : colors.text, margin: "0 0 10px 0", fontSize: "18px", fontWeight: "800" }}>
                    {item.title}
                  </h3>
                  
                  {item.isSpecial && (
                    <div style={{ 
                      backgroundColor: `${colors.brand}15`, 
                      padding: '14px', 
                      borderRadius: '12px', 
                      borderLeft: `4px solid ${colors.brand}`,
                      marginBottom: '15px',
                      color: isDarkMode ? "#fff" : colors.text,
                      fontSize: '13px',
                      fontWeight: '700',
                      lineHeight: '1.4'
                    }}>
                      <span style={{ color: colors.brand }}>MANDATORY:</span> Complete exactly 1 order per day for 3 consecutive days to establish network trust.
                    </div>
                  )}

                  <p style={{ 
                    color: isDarkMode ? "#94a3b8" : colors.text, 
                    opacity: 0.9, 
                    fontSize: "14px", 
                    lineHeight: "1.7", 
                    whiteSpace: "pre-line",
                    margin: 0
                  }}>
                    {item.text}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* FOOTER SECTION */}
        <div style={{ marginTop: "80px", textAlign: "center" }}>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '25px' }}>
             {['SECURITY VERIFIED', 'ENCRYPTED UPLINK', '24/7 MONITORING'].map((tag) => (
               <span key={tag} style={{ 
                 fontSize: '9px', 
                 padding: '7px 16px', 
                 borderRadius: '8px', 
                 border: isDarkMode ? `1px solid rgba(255,255,255,0.15)` : `1px solid ${colors.border}`,
                 color: isDarkMode ? "#cbd5e1" : colors.text,
                 backgroundColor: "rgba(255, 255, 255, 0.05)",
                 backdropFilter: "blur(8px)",
                 fontWeight: '900',
                 letterSpacing: '0.5px',
                 display: 'flex',
                 alignItems: 'center',
                 gap: '8px'
               }}>
                 <span className="glowing-dot"></span>
                 {tag}
               </span>
             ))}
          </div>
          
          <p style={{ color: colors.muted, fontSize: "11px", marginBottom: "25px", letterSpacing: '1px', fontWeight: '600' }}>
              © 2022 MARITIME SUPPLY SUITES | GLOBAL LOGISTICS DIVISION
          </p>
          
          <div style={{ 
            display: 'inline-block',
            padding: '22px 45px',
            borderRadius: '20px',
            backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.4)' : 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(20px) saturate(180%)',
            border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : `1px solid ${colors.border}`,
            boxShadow: isDarkMode ? '0 20px 50px rgba(0, 0, 0, 0.5)' : '0 10px 30px rgba(0,0,0,0.05)',
          }}>
            <div style={{ 
              color: isDarkMode ? "#f8fafc" : colors.text, 
              fontWeight: "800",
              fontSize: '15px',
              letterSpacing: '0.5px'
            }}>
              Safe shippings and high yields — 
              <a 
                href="https://maritimesupplysuites.shop" 
                target="_blank"
                rel="noreferrer"
                className="glowing-link"
                style={{ 
                  textDecoration: 'underline', 
                  color: colors.brand, 
                  marginLeft: '8px',
                  textShadow: `0 0 12px ${colors.brand}80, 0 0 25px ${colors.brand}40`,
                  transition: 'all 0.4s ease',
                  fontWeight: '900'
                }}
              >
                maritimesupplysuites.shop
              </a>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .glowing-dot {
          width: 6px; height: 6px; background-color: #00ff88; border-radius: 50%; display: inline-block; position: relative; box-shadow: 0 0 8px #00ff88; animation: dot-blink 2s infinite;
        }
        .glowing-dot::after {
          content: ''; position: absolute; top: -2px; left: -2px; right: -2px; bottom: -2px; border-radius: 50%; border: 1px solid #00ff88; animation: dot-pulse-ring 2s cubic-bezier(0.215, 0.61, 0.355, 1) infinite;
        }
        @keyframes dot-blink { 0% { opacity: 0.4; } 50% { opacity: 1; } 100% { opacity: 0.4; } }
        @keyframes dot-pulse-ring { 0% { transform: scale(0.33); opacity: 0.8; } 80%, 100% { transform: scale(2.5); opacity: 0; } }
        .floating-ship { animation: float 4s ease-in-out infinite; }
        @keyframes float { 0% { transform: translateY(0px) rotate(0deg); } 50% { transform: translateY(-15px) rotate(2deg); } 100% { transform: translateY(0px) rotate(0deg); } }
        .manual-card:hover {
          transform: translateY(-8px); border-color: ${colors.brand} !important; background-color: ${isDarkMode ? "rgba(255, 255, 255, 0.08)" : "#fff"} !important;
        }
        .glowing-link:hover { color: #fff !important; text-shadow: 0 0 15px ${colors.brand}, 0 0 30px ${colors.brand} !important; }
        .special-glow { animation: border-glow 3s infinite alternate; }
        @keyframes border-glow { from { box-shadow: 0 0 15px ${colors.brand}20; border-color: ${colors.brand}60; } to { box-shadow: 0 0 35px ${colors.brand}40; border-color: ${colors.brand}; } }
        @media (max-width: 768px) { .manual-card { padding: 22px !important; } .floating-ship { fontSize: 45px !important; } }
      `}</style>
    </div>
  );
}